import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Close,
  AccountTree,
  Layers,
  People,
  ReceiptLong,
  Tune,
  Engineering,
  TipsAndUpdates,
  NotificationsActive,
  DesignServices,
  CheckCircle,
  Link as LinkIcon,
  EventBusy,
  BeachAccess,
  Euro,
  Description,
  Handyman,
} from '@mui/icons-material';
import { useAuthContext } from 'Shared/Security/Domain/Contexts/authContext';
import {
  ORGANIGRAMA,
  EXTRAS,
  EXTRAS_REPARACIONES,
  CORPORATE_DOCUMENTS,
} from 'Shared/Routing/Application/Constants/PathsConstant';
import {
  PRIVATE_USER_LIST_USERS,
  PRIVATE_USER_VALIDATION_ORGCHARTS,
  IMPROVEMENT_CHANNEL,
  DELEGATION_SERVICE,
} from 'User/Application/Constants/PathsConstant';
import { PRIVATE_ADMIN_EXTRA_CONFIG } from 'Extra/AfterSalesExtra/Application/Constants/PathsConstant';
import { PRIVATE_ADMIN_EXTRA_INSTALLATION } from 'Extra/InstallationExtra/Application/Constants/PathsConstant';
import { PRIVATE_CS_ALERT_CONFIGURATION } from 'CS/Application/Constants/PathsConstant';
import {
  ADMIN_CS,
  READ_USERS,
  WRITE_USERS,
  GESTOR_DELEGACIONES_SERVICIOS,
  DELEGADO_DELEGACIONES_SERVICIOS,
  EXTRAS_MANTENIMIENTO,
  EXTRAS_EMPLOYE,
  EXTRAS_VALIDATOR,
  EXTRAS_REPARACIONES_EMPLEADO,
  EXTRAS_REPARACIONES_VALIDADOR,
  ADMIN_ORGCHART,
  ORGCHART,
  MANAGE_USER_PHOTOS,
} from 'Shared/Security/Application/Constants/PermissionsConstant';

export const getShortcutIcon = (iconName) => {
  switch (iconName) {
    case 'organigrama':
      return <AccountTree sx={{ fontSize: '2.2rem', color: 'white' }} />;
    case 'validationOrgCharts':
      return <Layers sx={{ fontSize: '2.2rem', color: 'white' }} />;
    case 'users':
      return <People sx={{ fontSize: '2.2rem', color: 'white' }} />;
    case 'extras':
    case 'variable':
      return <ReceiptLong sx={{ fontSize: '2.2rem', color: 'white' }} />;
    case 'extraConfig':
      return <Tune sx={{ fontSize: '2.2rem', color: 'white' }} />;
    case 'extraInstallation':
      return <Engineering sx={{ fontSize: '2.2rem', color: 'white' }} />;
    case 'improvement':
      return <TipsAndUpdates sx={{ fontSize: '2.2rem', color: 'white' }} />;
    case 'csAlerts':
      return <NotificationsActive sx={{ fontSize: '2.2rem', color: 'white' }} />;
    case 'services':
      return <DesignServices sx={{ fontSize: '2.2rem', color: 'white' }} />;
    // Legacy icon fallbacks
    case 'vacations':
      return <BeachAccess sx={{ fontSize: '2.2rem', color: 'white' }} />;
    case 'absenteeism':
      return <EventBusy sx={{ fontSize: '2.2rem', color: 'white' }} />;
    case 'expenses':
      return <Euro sx={{ fontSize: '2.2rem', color: 'white' }} />;
    case 'documents':
      return <Description sx={{ fontSize: '2.2rem', color: 'white' }} />;
    default:
      return <LinkIcon sx={{ fontSize: '2.2rem', color: 'white' }} />;
  }
};

const ShortcutConfigModal = ({ open, onClose, currentShortcuts = [], onSave }) => {
  const { checkPermission, isAdmin } = useAuthContext();
  const [selected, setSelected] = useState([]);

  const canAdminOrgChart = isAdmin() || checkPermission(ADMIN_ORGCHART) || checkPermission(ORGCHART);
  const hasValidationOrgChartPermission = canAdminOrgChart || checkPermission(EXTRAS_VALIDATOR);

  // Available shortcuts strictly matching the sections in Portal that the user has access to
  const availableShortcuts = useMemo(() => {
    return [
      {
        id: 'org-empresa',
        title: 'Organigrama Empresa',
        category: 'Organigrama',
        path: ORGANIGRAMA,
        icon: 'organigrama',
        iconComponent: AccountTree,
      },
      ...(hasValidationOrgChartPermission
        ? [
            {
              id: 'org-validaciones',
              title: 'Organigramas de validaciones',
              category: 'Organigrama',
              path: PRIVATE_USER_VALIDATION_ORGCHARTS,
              icon: 'validationOrgCharts',
              iconComponent: Layers,
            },
          ]
        : []),
      ...(checkPermission(WRITE_USERS) || checkPermission(READ_USERS) || checkPermission(MANAGE_USER_PHOTOS)
        ? [
            {
              id: 'org-usuarios',
              title: checkPermission(MANAGE_USER_PHOTOS) && !checkPermission(WRITE_USERS) ? 'Fotos de Usuarios' : 'Gestión de Usuarios',
              category: 'Organigrama',
              path: PRIVATE_USER_LIST_USERS,
              icon: 'users',
              iconComponent: People,
            },
          ]
        : []),
      ...(checkPermission(EXTRAS_MANTENIMIENTO) ||
      checkPermission(EXTRAS_EMPLOYE) ||
      checkPermission(EXTRAS_VALIDATOR) ||
      isAdmin()
        ? [
            {
              id: 'extras-mantenimiento',
              title: 'Extras Mantenimiento',
              category: 'Extras',
              path: EXTRAS,
              icon: 'extras',
              iconComponent: ReceiptLong,
            },
          ]
        : []),
      ...(checkPermission(EXTRAS_REPARACIONES_EMPLEADO) ||
      checkPermission(EXTRAS_REPARACIONES_VALIDADOR) ||
      isAdmin()
        ? [
            {
              id: 'extras-reparaciones',
              title: 'Extras Reparaciones',
              category: 'Extras',
              path: EXTRAS_REPARACIONES,
              icon: 'extrasRepair',
              iconComponent: Handyman,
            },
          ]
        : []),
      ...(checkPermission(EXTRAS_MANTENIMIENTO) || isAdmin()
        ? [
            {
              id: 'extras-config-posventa',
              title: 'Configuración Extras Posventa',
              category: 'Extras',
              path: PRIVATE_ADMIN_EXTRA_CONFIG,
              icon: 'extraConfig',
              iconComponent: Tune,
            },
            {
              id: 'extras-installation',
              title: 'Instalación Extra',
              category: 'Extras',
              path: PRIVATE_ADMIN_EXTRA_INSTALLATION,
              icon: 'extraInstallation',
              iconComponent: Engineering,
            },
          ]
        : []),
      {
        id: 'canal-mejora',
        title: 'Canal de Mejora',
        category: 'Canal de Mejora',
        path: IMPROVEMENT_CHANNEL,
        icon: 'improvement',
        iconComponent: TipsAndUpdates,
      },
      ...(checkPermission(ADMIN_CS)
        ? [
            {
              id: 'cs-alert-config',
              title: 'Configuración Alertas CS',
              category: 'Customer Service',
              path: PRIVATE_CS_ALERT_CONFIGURATION,
              icon: 'csAlerts',
              iconComponent: NotificationsActive,
            },
          ]
        : []),
      ...(checkPermission(GESTOR_DELEGACIONES_SERVICIOS) ||
      checkPermission(DELEGADO_DELEGACIONES_SERVICIOS)
        ? [
            {
              id: 'del-services-main',
              title: 'Delegación de Servicios',
              category: 'Servicios',
              path: DELEGATION_SERVICE,
              icon: 'services',
              iconComponent: DesignServices,
            },
          ]
        : []),
      {
        id: 'docs-corporativos',
        title: 'Documentos y Catálogos',
        category: 'Documentación',
        path: CORPORATE_DOCUMENTS,
        icon: 'documents',
        iconComponent: Description,
      },
    ];
  }, [checkPermission, isAdmin, hasValidationOrgChartPermission]);

  useEffect(() => {
    if (open) {
      // Preselect paths that match currently active accessible shortcuts
      const currentPaths = currentShortcuts
        .map((s) => s.path)
        .filter((p) => availableShortcuts.some((a) => a.path === p));
      setSelected(currentPaths);
    }
  }, [open, currentShortcuts, availableShortcuts]);

  const handleToggle = (item) => {
    if (selected.includes(item.path)) {
      setSelected(selected.filter((p) => p !== item.path));
    } else {
      if (selected.length >= 4) {
        return; // Maximum 4
      }
      setSelected([...selected, item.path]);
    }
  };

  const handleSave = () => {
    const newShortcuts = selected
      .map((path, index) => {
        const match = availableShortcuts.find((a) => a.path === path);
        if (!match) return null;
        return {
          title: match.title,
          path: match.path,
          icon: match.icon,
          position: index + 1,
        };
      })
      .filter(Boolean);

    onSave(newShortcuts);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
            Personalizar Accesos Directos
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.3 }}>
            Selecciona hasta 4 apartados del Portal para anclarlos en tu pantalla de inicio ({selected.length}/4)
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#64748b' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        <Grid container spacing={2}>
          {availableShortcuts.map((item) => {
            const isSelected = selected.includes(item.path);
            const isMaxReached = selected.length >= 4 && !isSelected;
            const IconCmp = item.iconComponent;

            return (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: '14px',
                    borderColor: isSelected ? '#257eb9' : '#e2e8f0',
                    borderWidth: isSelected ? '2px' : '1px',
                    bgcolor: isSelected ? '#f0f9ff' : '#ffffff',
                    opacity: isMaxReached ? 0.6 : 1,
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    height: '100%',
                    '&:hover': {
                      borderColor: isSelected ? '#1d6594' : '#93c5fd',
                      boxShadow: '0 4px 12px rgba(37, 126, 185, 0.1)',
                    },
                  }}
                >
                  <CardActionArea
                    disabled={isMaxReached}
                    onClick={() => handleToggle(item)}
                    sx={{
                      p: 2,
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      height: '100%',
                      justifyContent: 'space-between',
                    }}
                  >
                    {isSelected && (
                      <CheckCircle
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          color: '#257eb9',
                          fontSize: '1.25rem',
                        }}
                      />
                    )}

                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        bgcolor: isSelected ? '#257eb9' : '#f1f5f9',
                        color: isSelected ? '#ffffff' : '#257eb9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1.5,
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <IconCmp sx={{ fontSize: '1.6rem' }} />
                    </Box>

                    <Box sx={{ width: '100%' }}>
                      <Chip
                        label={item.category}
                        size="small"
                        sx={{
                          bgcolor: isSelected ? '#dbeafe' : '#f8fafc',
                          color: isSelected ? '#0369a1' : '#64748b',
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          height: '20px',
                          mb: 0.8,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isSelected ? 700 : 600,
                          color: isSelected ? '#0369a1' : '#1e293b',
                          fontSize: '0.86rem',
                          lineHeight: 1.3,
                        }}
                      >
                        {item.title}
                      </Typography>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, px: 3, justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
          {selected.length === 4
            ? 'Has alcanzado el límite máximo de 4 accesos directos.'
            : `Puedes seleccionar ${4 - selected.length} acceso${4 - selected.length > 1 ? 's' : ''} más.`}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 600 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={selected.length === 0}
            sx={{
              bgcolor: '#257eb9',
              '&:hover': { bgcolor: '#1d6594' },
              borderRadius: '10px',
              px: 3,
              textTransform: 'none',
              fontWeight: 700,
              boxShadow: '0 2px 8px rgba(37, 126, 185, 0.25)',
            }}
          >
            Guardar ({selected.length})
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

ShortcutConfigModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentShortcuts: PropTypes.array,
  onSave: PropTypes.func.isRequired,
};

export default ShortcutConfigModal;
